/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import debug from 'debug';

import {
    DETAILS,
    DIV,
    H2,
    H3,
    LABEL,
    LI,
    SUMMARY,
    UL,
} from 'sdi/components/elements';
import tr, { fromRecord, Translated } from 'sdi/locale';
import { IMapBaseLayer, MessageRecord } from 'sdi/source';

import {
    getBaseLayerCategories,
    getBaseLayerLicenseInfo,
    getBaseLayerResponsibleOrganisationNames,
    getBaseLayersForCategory,
    getBaseLayersWithoutCategory,
    getCurrentBaseLayerOpt,
} from '../../queries/app';
import { setMapBaseLayer } from '../../events/app';
import { icon } from 'sdi/components/button';
import { notEmpty } from 'sdi/util';
import { none, some } from 'fp-ts/lib/Option';

const logger = debug('sdi:webservices');

const renderSelectedBaseLayer = (baselayer: IMapBaseLayer) =>
    DIV(
        'base-layer-selected',
        DIV('label-wrapper', LABEL('', fromRecord(baselayer.name))),
        renderBaseLayerInfo(baselayer),
    );

const renderSelected = () =>
    DIV(
        'selected-base-layers',
        H3('', tr.view('selectedBaselayersTitle')),
        getCurrentBaseLayerOpt().map(renderSelectedBaseLayer),
    );

const isSelectedBaselayer = (baselayer: IMapBaseLayer) =>
    getCurrentBaseLayerOpt()
        .map(current => current.id === baselayer.id)
        .getOrElse(false);

const renderNamedList = (name: Translated, items: MessageRecord[]) =>
    DIV(
        'kv layer-name',
        DIV('key label', name),
        UL('layer-name__list', ...items.map(item => LI('', fromRecord(item)))),
    );

const renderBaseLayerInfo = (baseLayer: IMapBaseLayer) =>
    DETAILS(
        'download-table',
        SUMMARY('btn btn-3 icon-only', icon('info')),
        DIV(
            'download__body',
            getBaseLayerResponsibleOrganisationNames(baseLayer).map(
                organisations =>
                    renderNamedList(
                        'Organisations responsables' as Translated,
                        organisations,
                    ),
            ),
            getBaseLayerLicenseInfo(baseLayer).map(licenseInfo =>
                renderNamedList(
                    'Informations de license' as Translated,
                    licenseInfo,
                ),
            ),
        ),
    );

const renderBaseLayer = (
    add: (baselayerId: string) => void,
    selected: boolean,
    baseLayer: IMapBaseLayer,
) =>
    DIV(
        'base-layer__item__wrapper',
        selected
            ? DIV(
                  {
                      className: 'base-layer-item disabled',
                  },
                  icon('check'),
                  LABEL('', fromRecord(baseLayer.name)),
              )
            : DIV(
                  {
                      className: 'base-layer-item',
                      onClick: () => add(baseLayer.id),
                  },
                  icon('check'),
                  LABEL('', fromRecord(baseLayer.name)),
              ),
        getBaseLayerResponsibleOrganisationNames(baseLayer).isSome() ||
            getBaseLayerLicenseInfo(baseLayer).isSome()
            ? some(renderBaseLayerInfo(baseLayer))
            : none,
    );

const renderCategoriesList = () =>
    getBaseLayerCategories().map(category =>
        notEmpty(getBaseLayersForCategory(category)).map(baselayers =>
            DETAILS(
                'webservice',
                SUMMARY('webservice-name', fromRecord(category.name)),
                DIV(
                    'base-layer-list',
                    ...baselayers.map(bl =>
                        renderBaseLayer(
                            setMapBaseLayer,
                            isSelectedBaselayer(bl),
                            bl,
                        ),
                    ),
                ),
            ),
        ),
    );

const renderWithoutCategories = () =>
    notEmpty(getBaseLayersWithoutCategory()).map(baselayers =>
        DETAILS(
            'webservice',
            SUMMARY('webservice-name', tr.core('others')),
            DIV(
                'base-layer-list',
                ...baselayers.map(bl =>
                    renderBaseLayer(
                        setMapBaseLayer,
                        isSelectedBaselayer(bl),
                        bl,
                    ),
                ),
            ),
        ),
    );

const renderAllBaseLayers = () =>
    DIV(
        'base-layer-list',
        DIV('with-category', ...renderCategoriesList()),
        DIV('without-category', renderWithoutCategories()),
    );

const webservices = () => {
    return DIV(
        'sidebar-main tool wms-picker',
        H2({}, tr.view('wmsSwitch')),
        DIV({}, tr.view('helptext:wmsSwitchTool')),
        DIV(
            'tool-body',
            renderSelected(),
            DIV(
                'available-base-layers',
                H3('', tr.view('availableBaselayersTitle')),
                renderAllBaseLayers(),
            ),
        ),
    );
};

export default webservices;

logger('loaded');
